import React, { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { api } from "../lib/api.js";
import { useAuth } from "../lib/auth.jsx";
import { fmtDateTime } from "../lib/format.js";
import StatusBadge, { EscalationBadge } from "./StatusBadge.jsx";
import CommentThread from "./CommentThread.jsx";
import CompleteTaskDialog from "./CompleteTaskDialog.jsx";
import NewTaskDialog from "./NewTaskDialog.jsx";

const TASK_MANAGER_ROLES = ["admin", "national_aftersales_manager", "department_manager"];

/**
 * Tab listing linked tasks for a service case OR any linked record, with
 * inline Concluir / Reabrir action buttons (RBAC-gated) and an embedded
 * CommentThread per expanded row.
 *
 * Extracted from ServiceCaseDetailPage.jsx in iteration 42; extended in
 * iteration 49 to also filter by linked_module + linked_record_id so the
 * SWC / MPM / PCP detail pages can reuse it.
 */
export default function TasksTab({ caseId, linkedModule, linkedRecordId, users, refreshKey = 0 }) {
  const { user } = useAuth();
  const isManager = TASK_MANAGER_ROLES.includes(user?.role);
  const isReadOnly = user?.role === "read_only_viewer";
  const [tasks, setTasks] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [dialogTask, setDialogTask] = useState(null); // { task, mode }

  const load = async () => {
    const qs = new URLSearchParams();
    if (caseId) qs.set("service_case_id", caseId);
    if (linkedModule) qs.set("linked_module", linkedModule);
    if (linkedRecordId) qs.set("linked_record_id", linkedRecordId);
    const { data } = await api.get(`/tasks?${qs.toString()}`);
    setTasks(data);
  };
  useEffect(() => { load(); }, [caseId, linkedModule, linkedRecordId, refreshKey]);

  const canAct = (t) => {
    if (isReadOnly) return false;
    if (isManager) return true;
    return t.responsible_user_id === user?.id;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#1A1A2E]">Linked tasks ({tasks.length})</h3>
        <button onClick={() => setShowNew(true)} className="text-xs bg-[#1F3864] text-white px-2.5 py-1.5 rounded-sm hover:bg-[#142542] flex items-center gap-1" data-testid="new-task-btn">
          <Plus className="w-3 h-3" /> New task
        </button>
      </div>
      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F6F8] text-[10px] uppercase tracking-wider text-[#6C757D]">
            <tr>
              <th className="px-3 py-2 text-left">Task</th>
              <th className="px-3 py-2 text-left">Title</th>
              <th className="px-3 py-2 text-left">Responsible</th>
              <th className="px-3 py-2 text-left">Deadline</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Escal.</th>
            </tr>
          </thead>
          <tbody>
            {tasks.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-[#6C757D]">No tasks</td></tr>
            ) : tasks.map((t) => {
              const u = users.find((x) => x.id === t.responsible_user_id);
              const open = expanded === t.id;
              const isClosed = ["Concluída", "Cancelada"].includes(t.status);
              const showComplete = canAct(t) && !isClosed;
              const showReopen = canAct(t) && t.status === "Concluída";
              return (
                <React.Fragment key={t.id}>
                  <tr
                    className={`border-t border-gray-100 hover:bg-[#F8F9FA] cursor-pointer ${open ? "bg-[#F4F6F8]" : ""}`}
                    onClick={() => setExpanded(open ? null : t.id)}
                    data-testid={`task-row-${t.id}`}
                  >
                    <td className="px-3 py-2 font-mono text-xs text-[#1F3864]">{t.number}</td>
                    <td className="px-3 py-2">{t.title}</td>
                    <td className="px-3 py-2 text-xs">{u?.name || "—"}</td>
                    <td className="px-3 py-2 text-xs">{fmtDateTime(t.deadline)}</td>
                    <td className="px-3 py-2"><StatusBadge status={t.status} /></td>
                    <td className="px-3 py-2"><EscalationBadge level={t.escalation_level} /></td>
                  </tr>
                  {open && (
                    <tr className="bg-[#FAFBFC] border-t border-gray-100">
                      <td colSpan={6} className="px-4 py-3">
                        <div className="space-y-3">
                          {(showComplete || showReopen) && (
                            <div className="flex flex-wrap items-center gap-2" data-testid={`task-actions-${t.id}`}>
                              {showComplete && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setDialogTask({ task: t, mode: "complete" }); }}
                                  className="text-xs bg-[#28A745] hover:bg-[#1E7E34] text-white px-3 py-1.5 rounded-sm font-medium"
                                  data-testid={`complete-task-btn-${t.id}`}
                                >
                                  Concluir
                                </button>
                              )}
                              {showReopen && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setDialogTask({ task: t, mode: "reopen" }); }}
                                  className="text-xs bg-[#B45309] hover:bg-[#92400E] text-white px-3 py-1.5 rounded-sm font-medium"
                                  data-testid={`reopen-task-btn-${t.id}`}
                                >
                                  Reabrir
                                </button>
                              )}
                              {t.status === "Concluída" && t.completion_notes && (
                                <span className="text-xs text-[#6C757D] italic">
                                  Concluída por {t.completed_by_name || "—"} · {t.completion_notes}
                                </span>
                              )}
                            </div>
                          )}
                          <div id={`task-${t.id}`} className="bg-white border border-gray-200 rounded-md p-3">
                            <CommentThread recordType="task" recordId={t.id} compact />
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {showNew && <NewTaskDialog caseId={caseId} users={users} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
      {dialogTask && (
        <CompleteTaskDialog
          task={dialogTask.task}
          mode={dialogTask.mode}
          onClose={() => setDialogTask(null)}
          onDone={() => { setDialogTask(null); load(); }}
        />
      )}
    </div>
  );
}
